/**
 * UserChannelBoard — Kanban DnD channel config for per-user per-machine visibility.
 *
 * 4 mutually-exclusive zones (a channel lives in exactly one):
 *
 *  ┌──────────────────────────────────────────────────────┐
 *  │  POOL  —  channels excluded from this user           │
 *  │  [≡ Compressor 3]  [≡ HP1]  [≡ OLR]  …             │
 *  └──────────────────────────────────────────────────────┘
 *
 *  ┌───────────────┐  ┌───────────────┐  ┌───────────────┐
 *  │   VISIBLE     │  │  DASHBOARD    │  │     KPI       │
 *  │ Status/Reports│  │  (max 3 pills)│  │  Hourly charts│
 *  │ ≡ 1 Temp     │  │ ≡ 1 Dew Point│  │ ≡ 1 Water Lvl│
 *  │ ≡ 2 Humidity │  │ ≡ 2 Run Time  │  │               │
 *  └───────────────┘  └───────────────┘  └───────────────┘
 *
 * Saving semantics:
 *   visible_channels   = visible ∪ dashboard ∪ kpi
 *   dashboard_channels = dashboard
 *   kpi_channels       = kpi
 *
 * Remove behaviour:
 *   × on Dashboard/KPI  →  item moves to Visible (still shown, just not a metric)
 *   × on Visible        →  item moves to Pool (hidden from user)
 *
 * Props
 *   available          string[]  all channels for this machine
 *   visibleChannels    string[]
 *   dashboardChannels  string[]
 *   kpiChannels        string[]
 *   onChange           (field: string, value: any) => void
 *     fields: visible_channels | dashboard_channels | kpi_channels
 */
import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDragIndicator, MdClose } from 'react-icons/md';

// ── Zone metadata ─────────────────────────────────────────────────────────────

const ZONES = ['visible', 'dashboard', 'kpi'];

const ZONE_META = {
  visible:   { title: 'Visible',   sub: 'Shown in status & reports — empty = all channels', accent: '#2563eb', bg: '#eff6ff', cap: null },
  dashboard: { title: 'Dashboard', sub: 'Metric pills — max 3',                             accent: '#059669', bg: '#ecfdf5', cap: 3 },
  kpi:       { title: 'KPI',       sub: 'Hourly trend charts',                              accent: '#d97706', bg: '#fffbeb', cap: null },
};

// ── Pool chip ─────────────────────────────────────────────────────────────────

const PoolChip = ({ id }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id, data: { type: 'pool' },
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 6, padding: '5px 10px 5px 6px',
        cursor: 'grab', userSelect: 'none',
        fontSize: 12.5, fontWeight: 500, color: '#374151',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.06)'; }}
      {...attributes}
      {...listeners}
    >
      <MdDragIndicator size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
      {id}
    </div>
  );
};

// ── Column item ───────────────────────────────────────────────────────────────

const ColumnItem = ({ id, zone, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id, data: { type: 'zone', zone },
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        display: 'flex', alignItems: 'center', gap: 6,
        background: '#fff', border: '1px solid #f0f2f5',
        borderRadius: 7, padding: '7px 8px', marginBottom: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <span {...attributes} {...listeners}
        style={{ cursor: 'grab', color: '#c8d0da', display: 'flex', flexShrink: 0 }}>
        <MdDragIndicator size={15} />
      </span>
      <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 16, textAlign: 'right', flexShrink: 0 }}>
        {index + 1}
      </span>
      <span style={{
        flex: 1, fontSize: 13, fontWeight: 500, color: '#1e293b',
        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {id}
      </span>
      <button
        onClick={() => onRemove(id, zone)}
        title={zone === 'visible' ? 'Hide from user' : 'Move to Visible-only'}
        style={{
          border: 'none', background: 'none', cursor: 'pointer',
          color: '#c8d0da', padding: 2, display: 'flex',
          alignItems: 'center', borderRadius: 4, flexShrink: 0, lineHeight: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = zone === 'visible' ? '#ef4444' : '#f59e0b'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#c8d0da'; }}
      >
        <MdClose size={13} />
      </button>
    </div>
  );
};

// ── Zone column ───────────────────────────────────────────────────────────────

const ZoneColumn = ({ zone, items, onRemove }) => {
  const meta = ZONE_META[zone];
  const { setNodeRef, isOver } = useDroppable({ id: zone });
  const atCap = meta.cap != null && items.length >= meta.cap;

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: '#fff', borderRadius: 10,
      border: `1.5px solid ${(isOver && !atCap) ? meta.accent : '#e8ecf0'}`,
      boxShadow: (isOver && !atCap)
        ? `0 0 0 3px ${meta.accent}1a, 0 1px 4px rgba(0,0,0,0.06)`
        : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'border-color 0.12s, box-shadow 0.12s',
      minHeight: 180, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 12px 8px', background: meta.bg,
        borderBottom: '1px solid #f0f2f5', borderRadius: '8px 8px 0 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: meta.accent }}>{meta.title}</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
            background: atCap ? '#fef3c7' : `${meta.accent}1a`,
            color: atCap ? '#92400e' : meta.accent,
          }}>
            {items.length}{meta.cap != null ? `/${meta.cap}` : ''}
          </span>
        </div>
        <div style={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.3 }}>{meta.sub}</div>
        {atCap && <div style={{ fontSize: 10, color: '#d97706', marginTop: 3, fontWeight: 600 }}>⚠ Max reached</div>}
      </div>
      {/* Items */}
      <div ref={setNodeRef} style={{ flex: 1, padding: '8px 8px 4px', minHeight: 60 }}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((ch, idx) => (
            <ColumnItem key={ch} id={ch} zone={zone} index={idx} onRemove={onRemove} />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <div style={{
            height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1.5px dashed ${isOver ? meta.accent : '#dde3ea'}`,
            borderRadius: 7, fontSize: 12,
            color: isOver ? meta.accent : '#b0b8c4',
            fontWeight: isOver ? 600 : 400,
            transition: 'all 0.12s', userSelect: 'none',
          }}>
            {isOver ? '↓ Drop here' : 'Drag channels here'}
          </div>
        )}
      </div>
    </div>
  );
};

// ── UserChannelBoard ──────────────────────────────────────────────────────────

const UserChannelBoard = ({
  available = [],
  visibleChannels = [],
  dashboardChannels = [],
  kpiChannels = [],
  onChange,
}) => {
  const buildItems = () => {
    const dashSet    = new Set(dashboardChannels);
    const kpiSet     = new Set(kpiChannels);
    const visibleSet = new Set(visibleChannels);
    return {
      pool:      available.filter((ch) => !visibleSet.has(ch)),
      visible:   visibleChannels.filter((ch) => !dashSet.has(ch) && !kpiSet.has(ch)),
      dashboard: dashboardChannels.slice(0, 3),
      kpi:       kpiChannels,
    };
  };

  const [items,    setItems]    = useState(buildItems);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const allZones = ['pool', ...ZONES];

  const findContainer = useCallback((id) => {
    if (allZones.includes(id)) return id;
    for (const key of allZones) {
      if (items[key]?.includes(id)) return key;
    }
    return null;
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute and fire onChange
  const fireChange = useCallback((newItems) => {
    const visibleAll = [...newItems.visible, ...newItems.dashboard, ...newItems.kpi];
    onChange('visible_channels',   visibleAll);
    onChange('dashboard_channels', newItems.dashboard);
    onChange('kpi_channels',       newItems.kpi);
  }, [onChange]);

  const onDragStart = ({ active }) => setActiveId(active.id);

  const onDragOver = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const activeContainer = findContainer(active.id);
    const overContainer   = findContainer(over.id);

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    // Enforce dashboard cap
    if (overContainer === 'dashboard') {
      if (items.dashboard.length >= 3 && !items.dashboard.includes(active.id)) return;
    }

    setItems((prev) => {
      const sourceItems = prev[activeContainer].filter((id) => id !== active.id);
      const destItems   = [...prev[overContainer]];
      const overIndex   = destItems.indexOf(over.id);
      const insertAt    = overIndex >= 0 ? overIndex : destItems.length;
      destItems.splice(insertAt, 0, active.id);
      return { ...prev, [activeContainer]: sourceItems, [overContainer]: destItems };
    });
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const activeContainer = findContainer(active.id);
    const overContainer   = findContainer(over.id);
    if (!activeContainer || !overContainer) return;

    if (activeContainer === overContainer) {
      const idx1 = items[activeContainer].indexOf(active.id);
      const idx2 = items[activeContainer].indexOf(over.id);
      if (idx1 !== -1 && idx2 !== -1 && idx1 !== idx2) {
        setItems((prev) => {
          const newItems = {
            ...prev,
            [activeContainer]: arrayMove(prev[activeContainer], idx1, idx2),
          };
          if (activeContainer !== 'pool') fireChange(newItems);
          return newItems;
        });
        return;
      }
    }
    if (activeContainer !== 'pool' || overContainer !== 'pool') {
      fireChange(items);
    }
  };

  // × removal — dashboard/kpi → visible; visible → pool
  const onRemove = useCallback((channelId, zone) => {
    setItems((prev) => {
      let newItems;
      if (zone === 'visible') {
        newItems = {
          ...prev,
          visible: prev.visible.filter((ch) => ch !== channelId),
          pool:    [...prev.pool, channelId],
        };
      } else {
        // dashboard or kpi → back to visible (still shown to user, just not a metric)
        newItems = {
          ...prev,
          [zone]:   prev[zone].filter((ch) => ch !== channelId),
          visible: [...prev.visible, channelId],
        };
      }
      fireChange(newItems);
      return newItems;
    });
  }, [fireChange]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {/* Pool */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: '#6b7280',
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
        }}>
          Hidden from user — drag to sections below to grant access
        </div>
        <div style={{
          background: '#f8fafc', border: '1.5px dashed #cbd5e1',
          borderRadius: 8, padding: '10px 12px', minHeight: 44,
        }}>
          <SortableContext items={items.pool} strategy={rectSortingStrategy}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {items.pool.map((ch) => <PoolChip key={ch} id={ch} />)}
              {items.pool.length === 0 && (
                <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '2px 0' }}>
                  ✓ All channels are configured
                </span>
              )}
            </div>
          </SortableContext>
        </div>
      </div>

      {/* 3-column zone grid */}
      <div style={{ display: 'flex', gap: 10 }}>
        {ZONES.map((zone) => (
          <ZoneColumn
            key={zone}
            zone={zone}
            items={items[zone]}
            onRemove={onRemove}
          />
        ))}
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeId ? (
          <div style={{
            background: '#fff', border: '1.5px solid #3b82f6',
            borderRadius: 7, padding: '6px 12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            fontSize: 13, fontWeight: 600, color: '#1e293b',
            display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'grabbing',
          }}>
            <MdDragIndicator size={16} style={{ color: '#3b82f6' }} />
            {activeId}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default UserChannelBoard;
